/**
 * useWorkOrderWorkflow - Custom hook managing the 3-step work order completion workflow.
 *
 * Handles:
 *   1. Fetching and caching checklist state (online + offline snapshots)
 *   2. Toggling checklist items with optimistic updates
 *   3. Polling for real-time updates from other technicians
 *   4. Offline storage of checklist progress via IndexedDB
 *   5. Step navigation (Checklist -> Verify -> Sign Off)
 *   6. Verification photo collection and sign-off submission
 *
 * The checklist READ is owned by TanStack Query (keyed via qk.workOrderWorkflow):
 *   - It participates in the shared, persisted query cache and post-sync
 *     invalidation, like every other read in the app.
 *   - Polling is expressed declaratively via refetchInterval.
 *   - `enabled` is gated on `isOpen` so we don't fetch for a closed overlay.
 * The offline-first nuances are preserved INSIDE the queryFn + mutations:
 *   - When online, fresh data is fetched and snapshotted to IndexedDB.
 *   - When offline (or the server is unreachable), the IndexedDB snapshot is
 *     returned so the tech can keep working; only a total absence of cached
 *     data surfaces as an error.
 *   - Optimistic toggles/verify/sign-off write through queryClient.setQueryData
 *     and continue to snapshot + queue pending items for sync exactly as before.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    checklistService,
    WorkOrderChecklist,
} from '../services/checklistService';
import { checklistInstanceService } from '../services/checklistInstanceService';
import type { CapturedPhoto } from '../components/organisms/PhotoCapture/PhotoCapture';
import {
    saveChecklistSnapshot,
    getChecklistSnapshot,
    addPendingItem,
    STORE_NAMES,
} from '../services/offlineStorage';
import { qk } from '../lib/queryKeys';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** The three sequential steps in the workflow */
export type WorkflowStep = 'checklist' | 'verify' | 'sign-off';

/**
 * Return type of the useWorkOrderWorkflow hook.
 * Exposes all state and actions needed by the workflow UI components.
 */
export interface WorkflowState {
    /** Current checklist data (items, verification, submission status) */
    checklist: WorkOrderChecklist | null;
    /** Whether the initial load is in progress */
    isLoading: boolean;
    /** Error message from the last failed operation */
    error: string | null;
    /** Current active step in the workflow */
    currentStep: WorkflowStep;
    /**
     * Whether the current user is allowed to verify + sign off this WO.
     * True when the user is either the primary assignee OR has an
     * elevated role (super_admin / admin / company_admin) that permits
     * acting on behalf of the team. The parent component owns the
     * role check and passes the result into the hook so this stays a
     * single boolean gate for the UI to consume.
     */
    canActAsPrimary: boolean;
    /** Whether all checklist items are completed */
    allItemsCompleted: boolean;
    /** Photos collected during the verification step */
    verificationPhotos: CapturedPhoto[];
    /** Whether a submission is in progress */
    isSubmitting: boolean;
    /** Whether the device is currently offline */
    isOffline: boolean;

    /** Toggle a checklist item's completion status */
    toggleItem: (itemId: string) => Promise<void>;
    /**
     * Add an ad-hoc item to THIS work order's checklist — the work the crew
     * actually did that the template never listed ("Rooms 201-220"). Lands as
     * an optional item so a late addition can't block sign-off. Requires a
     * connection; see the implementation for why.
     */
    addItem: (title: string) => Promise<void>;
    /** Navigate to a specific workflow step */
    goToStep: (step: WorkflowStep) => void;
    /** Add a photo during the verification step */
    addVerificationPhoto: (photo: CapturedPhoto) => void;
    /** Remove a verification photo by index */
    removeVerificationPhoto: (index: number) => void;
    /** Submit the verification (photos confirm work is complete) */
    submitVerification: () => Promise<void>;
    /** Final sign-off with signature */
    submitSignOff: (signatureDataUrl: string, notes?: string) => Promise<void>;
    /** Force refresh the checklist from the server */
    refresh: () => Promise<void>;
}

/* ============================================
 * CONSTANTS
 * ============================================ */

/** How often to poll for updates from other technicians (in milliseconds) */
const POLL_INTERVAL_MS = 10_000;

/**
 * loadChecklist() - queryFn for the workflow read.
 *
 * Online: fetch from the API and snapshot to IndexedDB for offline access.
 * Offline / unreachable: fall back to the most recent IndexedDB snapshot so the
 * tech can keep working. Throws only when there is no data at all to show.
 */
async function loadChecklist(workOrderId: string): Promise<WorkOrderChecklist> {
    if (navigator.onLine) {
        try {
            const response = await checklistService.getChecklist(workOrderId);
            await saveChecklistSnapshot(
                workOrderId,
                response.data as unknown as Record<string, unknown>
            );
            return response.data;
        } catch (err) {
            // Network/API failure — fall back to cache if we have it.
            const snapshot = await getChecklistSnapshot(workOrderId);
            if (snapshot) return snapshot as unknown as WorkOrderChecklist;
            throw err instanceof Error ? err : new Error('Failed to load checklist.');
        }
    }
    // Offline: cache only.
    const snapshot = await getChecklistSnapshot(workOrderId);
    if (snapshot) return snapshot as unknown as WorkOrderChecklist;
    throw new Error('No cached data available. Please connect to the internet to load the checklist.');
}

/* ============================================
 * HOOK
 * ============================================ */

/**
 * useWorkOrderWorkflow - Manages the complete workflow lifecycle for a work order.
 *
 * @param workOrderId - The work order to load the checklist for
 * @param currentUserId - The logged-in user's ID (to determine primary contact status)
 * @param isOpen - Whether the workflow overlay is currently visible (controls polling)
 * @param currentUserCanActAsPrimary - True when the current user holds an
 *   elevated role (super_admin / admin / company_admin) and may therefore
 *   verify + sign off on the team's behalf, even without being the
 *   primary assignee. Defaults to false so legacy callers preserve the
 *   old "primary-only" gate until they opt in.
 * @returns WorkflowState object with all state and actions
 */
export function useWorkOrderWorkflow(
    workOrderId: string,
    currentUserId: string,
    isOpen: boolean,
    currentUserCanActAsPrimary: boolean = false
): WorkflowState {
    const queryClient = useQueryClient();
    const checklistKey = qk.workOrderWorkflow(workOrderId);

    // --- UI state (not server-owned) ---
    const [currentStep, setCurrentStep] = useState<WorkflowStep>('checklist');
    const [verificationPhotos, setVerificationPhotos] = useState<CapturedPhoto[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    // Local error for mutation failures; combined with the query error below.
    const [actionError, setActionError] = useState<string | null>(null);

    // --- Monitor online/offline status ---
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // --- Checklist read (TanStack Query) ---
    // Only runs while the overlay is open. Polls every POLL_INTERVAL_MS while
    // online and parked on the checklist step (to pick up other techs' toggles).
    const query = useQuery<WorkOrderChecklist>({
        queryKey: checklistKey,
        queryFn: () => loadChecklist(workOrderId),
        enabled: isOpen,
        refetchInterval: () =>
            isOpen && !isOffline && currentStep === 'checklist' ? POLL_INTERVAL_MS : false,
    });

    const checklist: WorkOrderChecklist | null = query.data ?? null;
    const isLoading = isOpen && query.isLoading;
    // Surface the read error (e.g. no cached data) or the last mutation error.
    const error = actionError ?? (query.error ? (query.error as Error).message : null);

    /** Write a new checklist into the query cache (replaces old setChecklist). */
    const setChecklist = useCallback(
        (next: WorkOrderChecklist) => {
            queryClient.setQueryData(checklistKey, next);
        },
        [queryClient, checklistKey]
    );

    // --- Derived state ---
    // Determine if the current user is the primary contact for this WO,
    // OR holds an elevated role (super_admin / admin / company_admin)
    // that permits verifying + signing off on the team's behalf.
    const isPrimaryAssignee = checklist?.assignees.some(
        (a) => a.userId === currentUserId && a.isPrimary
    ) ?? false;
    const canActAsPrimary = isPrimaryAssignee || currentUserCanActAsPrimary;

    // Required-only completion gate. Optional items don't block sign-off.
    const requiredItems = checklist?.items.filter((item) => item.isRequired) ?? [];
    const allItemsCompleted = requiredItems.length === 0
        ? (checklist?.items.length ?? 0) > 0 && (checklist?.items.every((item) => item.isCompleted) ?? false)
        : requiredItems.every((item) => item.isCompleted);

    // --- Determine initial step based on existing progress ---
    // If the user returns to an already-verified checklist, jump to sign-off
    useEffect(() => {
        if (!checklist) return;
        if (checklist.isSubmitted) {
            setCurrentStep('sign-off');
        } else if (checklist.isVerified) {
            setCurrentStep('sign-off');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checklist?.isVerified, checklist?.isSubmitted]);

    /**
     * toggleItem() - Toggles a checklist item's completion status.
     *
     * Uses optimistic updates: the cache changes immediately while the API call
     * happens in the background. If the API call fails (or we're offline),
     * the toggle is queued in IndexedDB for later sync.
     */
    const toggleItem = useCallback(async (itemId: string) => {
        if (!checklist) return;

        const item = checklist.items.find((i) => i.id === itemId);
        if (!item) return;

        const newCompleted = !item.isCompleted;

        // Optimistic update: toggle the item in the cache immediately
        const updatedItems = checklist.items.map((i) =>
            i.id === itemId
                ? {
                    ...i,
                    isCompleted: newCompleted,
                    completedById: newCompleted ? currentUserId : null,
                    completedByName: newCompleted ? 'You' : null,
                    completedAt: newCompleted ? new Date().toISOString() : null,
                }
                : i
        );

        const updatedChecklist: WorkOrderChecklist = {
            ...checklist,
            items: updatedItems,
        };

        setChecklist(updatedChecklist);

        // Cache the updated state to IndexedDB
        await saveChecklistSnapshot(
            workOrderId,
            updatedChecklist as unknown as Record<string, unknown>
        );

        if (navigator.onLine) {
            try {
                await checklistService.toggleItem(workOrderId, itemId, {
                    isCompleted: newCompleted,
                });
            } catch (err) {
                console.error('[useWorkOrderWorkflow] Toggle API error, queued for sync:', err);
                await addPendingItem(STORE_NAMES.CHECKLIST_UPDATES, {
                    workOrderId,
                    itemId,
                    isCompleted: newCompleted,
                    type: 'checklist_toggle',
                });
            }
        } else {
            await addPendingItem(STORE_NAMES.CHECKLIST_UPDATES, {
                workOrderId,
                itemId,
                isCompleted: newCompleted,
                type: 'checklist_toggle',
            });
        }
    }, [checklist, currentUserId, workOrderId, setChecklist]);

    /**
     * addItem() - Append an ad-hoc item to this work order's checklist.
     *
     * The crew's week of work rarely matches the template line-for-line, and
     * whatever they actually did has to be on the record before the customer
     * signs. The item is added to THIS work order only — the CMS template it
     * was snapshotted from is untouched — and the server creates the checklist
     * instance on demand if the WO never had one.
     *
     * Deliberately NOT optimistic, unlike toggleItem: the server assigns the
     * id and sort order, and a placeholder row that can't be checked off or
     * photographed is worse than a brief wait. For the same reason it refuses
     * to run offline — a queued item has no id, so nothing downstream (toggle,
     * photo attach, sign-off) could reference it until the sync landed.
     */
    const addItem = useCallback(async (title: string) => {
        const trimmed = title.trim();
        if (!trimmed) return;

        if (!navigator.onLine) {
            setActionError('You need a connection to add a checklist item. Reconnect and try again.');
            return;
        }

        try {
            setActionError(null);
            /* Sort after everything currently on the list so additions land at
               the bottom in the order the crew entered them. */
            const nextSortOrder = (checklist?.items ?? []).reduce(
                (max, it) => Math.max(max, it.sortOrder ?? 0),
                0
            ) + 1;
            await checklistInstanceService.addItemToWorkOrder(workOrderId, {
                title: trimmed,
                /* Optional by default — a required item added mid-job would
                   block the very sign-off the crew is adding it for. */
                isRequired: false,
                sortOrder: nextSortOrder,
            });
            /* Refetch rather than splice: the response is a bare instance item,
               not the WorkOrderChecklist shape this hook caches, and the
               queryFn is also what re-snapshots the list to IndexedDB. */
            await queryClient.invalidateQueries({ queryKey: checklistKey });
        } catch (err) {
            console.error('[useWorkOrderWorkflow] Add item error:', err);
            setActionError(err instanceof Error ? err.message : 'Failed to add item');
        }
    }, [checklist?.items, workOrderId, queryClient, checklistKey]);

    /**
     * goToStep() - Navigate to a specific workflow step.
     * Validates that the user can proceed to the requested step.
     */
    const goToStep = useCallback((step: WorkflowStep) => {
        if (step === 'checklist') {
            setCurrentStep('checklist');
            return;
        }

        if (step === 'verify') {
            if (!allItemsCompleted) {
                setActionError('All checklist items must be completed before verification.');
                return;
            }
            if (!canActAsPrimary) {
                setActionError('Only the primary technician or an admin can verify the checklist.');
                return;
            }
            setActionError(null);
            setCurrentStep('verify');
            return;
        }

        if (step === 'sign-off') {
            if (!checklist?.isVerified && !canActAsPrimary) {
                setActionError('The checklist must be verified before sign-off.');
                return;
            }
            setActionError(null);
            setCurrentStep('sign-off');
        }
    }, [allItemsCompleted, canActAsPrimary, checklist?.isVerified]);

    /** addVerificationPhoto() - Add a captured photo to the verification collection. */
    const addVerificationPhoto = useCallback((photo: CapturedPhoto) => {
        setVerificationPhotos((prev) => [...prev, photo]);
    }, []);

    /** removeVerificationPhoto() - Remove a photo from the verification collection by index. */
    const removeVerificationPhoto = useCallback((index: number) => {
        setVerificationPhotos((prev) => prev.filter((_, i) => i !== index));
    }, []);

    /**
     * submitVerification() - Submits verification with photos.
     * Marks the checklist as verified and advances to the sign-off step.
     */
    const submitVerification = useCallback(async () => {
        if (!checklist) return;

        setIsSubmitting(true);
        setActionError(null);

        try {
            if (navigator.onLine) {
                const response = await checklistService.verify(workOrderId, {
                    photos: verificationPhotos.map((p) => ({
                        base64: p.base64,
                        caption: p.fileName,
                    })),
                });

                setChecklist(response.data);
                await saveChecklistSnapshot(
                    workOrderId,
                    response.data as unknown as Record<string, unknown>
                );
            } else {
                const updatedChecklist: WorkOrderChecklist = {
                    ...checklist,
                    isVerified: true,
                    verifiedById: currentUserId,
                    verifiedByName: 'You',
                    verifiedAt: new Date().toISOString(),
                };

                setChecklist(updatedChecklist);
                await saveChecklistSnapshot(
                    workOrderId,
                    updatedChecklist as unknown as Record<string, unknown>
                );

                await addPendingItem(STORE_NAMES.CHECKLIST_UPDATES, {
                    workOrderId,
                    type: 'checklist_verify',
                    photos: verificationPhotos.map((p) => ({
                        base64: p.base64,
                        caption: p.fileName,
                    })),
                });
            }

            setCurrentStep('sign-off');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Verification failed';
            setActionError(message);
            console.error('[useWorkOrderWorkflow] Verification error:', err);
        } finally {
            setIsSubmitting(false);
        }
    }, [checklist, workOrderId, currentUserId, verificationPhotos, setChecklist]);

    /**
     * submitSignOff() - Final sign-off with signature data.
     * Submits the completed work order. This is the last step in the workflow.
     */
    const submitSignOff = useCallback(async (signatureDataUrl: string, notes?: string) => {
        if (!checklist) return;

        setIsSubmitting(true);
        setActionError(null);

        try {
            if (navigator.onLine) {
                const response = await checklistService.submit(workOrderId, {
                    signatureDataUrl,
                    notes,
                });

                setChecklist(response.data);
                await saveChecklistSnapshot(
                    workOrderId,
                    response.data as unknown as Record<string, unknown>
                );
            } else {
                const updatedChecklist: WorkOrderChecklist = {
                    ...checklist,
                    isSubmitted: true,
                    submittedById: currentUserId,
                    submittedByName: 'You',
                    submittedAt: new Date().toISOString(),
                    signatureDataUrl,
                };

                setChecklist(updatedChecklist);
                await saveChecklistSnapshot(
                    workOrderId,
                    updatedChecklist as unknown as Record<string, unknown>
                );

                await addPendingItem(STORE_NAMES.CHECKLIST_UPDATES, {
                    workOrderId,
                    type: 'checklist_submit',
                    signatureDataUrl,
                    notes,
                });
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Sign-off failed';
            setActionError(message);
            console.error('[useWorkOrderWorkflow] Sign-off error:', err);
        } finally {
            setIsSubmitting(false);
        }
    }, [checklist, workOrderId, currentUserId, setChecklist]);

    /** refresh() - Force refresh the checklist from the server. */
    const refresh = useCallback(async () => {
        await query.refetch();
    }, [query]);

    return {
        checklist,
        isLoading,
        error,
        currentStep,
        canActAsPrimary,
        allItemsCompleted,
        verificationPhotos,
        isSubmitting,
        isOffline,
        toggleItem,
        addItem,
        goToStep,
        addVerificationPhoto,
        removeVerificationPhoto,
        submitVerification,
        submitSignOff,
        refresh,
    };
}
